import React, { useState, useRef, useEffect } from "react";
import "./LoginForm.css";
import { FaUser, FaLock } from "react-icons/fa";
import FirmSelection from "./FirmSelection";
import axios from "axios";
import ErrorModal from "./ErrorModal";
import PopUpModal from "./PopUpModal";
import { useSearchParams, Link } from "react-router-dom";

function LoginForm() {
  const API_URL = process.env.REACT_APP_API_URL;

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isResetLoading, setIsResetLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [showFirmSelection, setShowFirmSelection] = useState(true);
  const [firmeList, setFirmeList] = useState([]);
  const [errorLog, setErrorLog] = useState(null);
  const [modalPopUp, setModalPopUp] = useState(null);
  const [searchParams] = useSearchParams();

  const loginDataRef = useRef({ IdKorisnika: null, GUID: null });
  const redirectStartedRef = useRef(false);
  const isFirmSelectionVisible = showFirmSelection && firmeList && firmeList.length > 1;

  useEffect(() => {
    const error = searchParams.get("error");
    if (error) setErrorLog(error);
  }, [searchParams]);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    redirectStartedRef.current = false;
    setIsLoading(true);
    setLoadingMessage("Provera podataka...");
    setErrorLog(null);

    try {
      const response = await axios.post(
        `${API_URL}/Login/Korisnik/Login`,
        { username, password }
      );

      if (response.status === 200) {
        const result = response.data;
        setPassword("");
        setUsername("");

        const guidLog = result.GUID;
        const idKorisnika = result.IdKorisnika;
        loginDataRef.current = { IdKorisnika: idKorisnika, GUID: guidLog };

        if (result.Firme && result.Firme.length > 0) {
          if (result.Firme.length === 1) {
            const singleFirm = result.Firme[0];
            try {
              setLoadingMessage("Logovanje u firmu...");
              const firmResponse = await axios.post(
                `${API_URL}/Login/Korisnik/LoginUFirmu`,
                {
                  IdKorisnika: idKorisnika,
                  GUID: guidLog,
                  IdFirme: singleFirm.Id,
                  IdBaze: singleFirm.IdBaze,
                },
                { headers: { "Content-Type": "application/json" } }
              );
              if (firmResponse.status === 200 && firmResponse.data) {
                setLoadingMessage("Preusmeravanje...");
                redirectStartedRef.current = true;
                window.history.replaceState(null, null, "/");
                window.location.replace(firmResponse.data);
              }
            } catch (err) {
              if (err.response?.status === 400) {
                setErrorLog(err.response.data.Message);
              } else {
                setErrorLog(
                  "Došlo je do greške u komunikaciji sa serverom. Pritisnite ctrl i f5 na tastaturi u isto vreme i pokušajte ponovo."
                );
              }
            }
          } else {
            setFirmeList(result.Firme);
            setShowFirmSelection(true);
          }
        }
      }
    } catch (error) {
      setErrorLog("Nemate odabranu firmu ili niste uneli ispravne podatke");
    } finally {
      if (!redirectStartedRef.current) {
        setIsLoading(false);
        setLoadingMessage("");
      }
    }
  };

  const handleFirmSelectionClose = () => {
    setShowFirmSelection(false);
    setFirmeList(null);
    setUsername("");
    setPassword("");
  };

  const handlePasswordReset = async () => {
    try {
      setIsResetLoading(true);
      const response = await axios.post(
        `${API_URL}/Login/Korisnik/ResetujSifru`,
        { username }
      );
      if (response.status === 200) {
        setModalPopUp("Uspešno poslat zahtev za resetovanje lozinke.");
      }
    } catch (error) {
      setErrorLog(
        "Za resetovanje lozinke morate upisati vaše korisničko ime u odgovarajuće polje i kliknuti na Zaboravljena lozinka. Uputstvo za resetovanje ćete dobiti na Vaš email."
      );
    } finally {
      setIsResetLoading(false);
    }
  };

  return (
    <div className="auth-layout">

      {/* ===== ORANGE PANEL (levo) ===== */}
      <div className="auth-panel auth-panel--left">
        <div>
          <h2>Zanima Vas Akord softver?</h2>
          <p>
            Popunite formu i mi ćemo vas kontaktirati radi dogovora
            prezentacije.
          </p>
        </div>
        <Link to="/registracija" className="btn-panel">
          Registrujte se
        </Link>
        <img
          src="/poslovni-softver/img/login.png"
          className="auth-panel-img"
          alt="Akord softver"
          loading="eager"
        />
      </div>

      {/* ===== LOGIN FORMA (desno) ===== */}
      <div className="auth-form-side auth-form--right">
        <h2 className="title">Prijava na sistem</h2>

        <form onSubmit={handleLoginSubmit}>
          <div className="input-field">
            <FaUser className="icon-login" />
            <input
              type="text"
              placeholder="Korisničko ime"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={isLoading || isResetLoading}
              required
            />
          </div>

          <div className="input-field">
            <FaLock className="icon-login" />
            <input
              type="password"
              placeholder="Lozinka"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading || isResetLoading}
              required
            />
          </div>

          <div className="login-actions">
            <button
              type="submit"
              className={`btn-login ${isLoading ? "btn-login--loading" : ""}`}
              disabled={isLoading || isResetLoading}
            >
              {isLoading && <span className="auth-spinner" aria-hidden="true" />}
              <span>{isLoading ? "Logovanje..." : "Logovanje"}</span>
            </button>
            {isLoading && loadingMessage && (
              <div className="auth-loading-status" role="status" aria-live="polite">
                {loadingMessage}
              </div>
            )}
            <button
              type="button"
              className="btn-reset"
              onClick={handlePasswordReset}
              disabled={isLoading || isResetLoading}
            >
              {isResetLoading ? "Slanje zahteva..." : "Zaboravljena lozinka?"}
            </button>
          </div>
        </form>
      </div>

      {showFirmSelection && firmeList && firmeList.length > 1 && (
        <FirmSelection
          firmeList={firmeList}
          onClose={handleFirmSelectionClose}
          loginDataRef={loginDataRef}
          setParentError={setErrorLog}
        />
      )}

      {isLoading && !isFirmSelectionVisible && (
        <div className="auth-loading-overlay" role="status" aria-live="polite">
          <div className="auth-loading-box">
            <span className="auth-overlay-spinner" aria-hidden="true" />
            <span>{loadingMessage || "Logovanje u toku..."}</span>
          </div>
        </div>
      )}

      {errorLog && (
        <ErrorModal message={errorLog} onClose={() => setErrorLog(null)} />
      )}
      {modalPopUp && (
        <PopUpModal message={modalPopUp} onClose={() => setModalPopUp(null)} />
      )}
    </div>
  );
}

export default LoginForm;
